/**
 * authorisationGate.ts — the LANE A override's admission test. PURE: no React,
 * no fetch, and `now` is injected so every boundary is testable.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: end-of-term re-subscribe.
 *
 * WHY THIS IS ITS OWN MODULE. Lane A is an admin recording that a patient
 * authorised a charge on a call — the audit row it writes is the ONLY evidence
 * that happened. What makes that evidence admissible is not a dialog layout, it
 * is a small set of conditions, and those conditions deserve to be readable,
 * testable and impossible to satisfy by accident. The server re-validates every
 * one of them; this exists so the admin is told what is missing instead of
 * meeting a 400.
 *
 * THE UI IS NEVER THE CONTROL. Nothing here is a security boundary — the
 * orders-service checks the method, the staleness and the attestation again,
 * against the verified ADMIN principal. This is the courtesy layer.
 *
 * THE ONE STRUCTURAL GUARANTEE: the wire block comes back ONLY on a passing
 * draft ({@link AuthorisationCheck.value}). A caller cannot assemble the
 * authorisation from the draft fields itself without deliberately going around
 * this function, so "submitted while incomplete" is not a bug that can be
 * introduced by a later edit to the dialog.
 */
import type { ResubscribeAuthorisationInput, ResubscribeAuthorisationMethod } from '../api/types';
import { MAX_AUTHORISATION_AGE_DAYS } from './resubscribeCopy';

/** Contracts caps the free-text call/ticket reference at 64 characters. */
export const AUTHORISATION_REFERENCE_MAX = 64;

/** What the dialog holds while the admin fills the override in. */
export interface AuthorisationDraft {
  /** No default, ever — an unselected radio group is what stops a mis-click submitting "phone call". */
  method: ResubscribeAuthorisationMethod | null;
  /** A `datetime-local` value (`yyyy-MM-ddTHH:mm`) in the ADMIN's own timezone, or ''. */
  takenAtLocal: string;
  /** Optional call/ticket reference. */
  reference: string;
  /** The explicit attestation. Starts unchecked. */
  attested: boolean;
}

export interface AuthorisationCheck {
  /** True only when every condition holds. */
  ready: boolean;
  /** Field-level message for the date control, or null. Shown, not just used to disable. */
  takenAtError: string | null;
  /** The wire block — non-null IF AND ONLY IF `ready`. */
  value: ResubscribeAuthorisationInput | null;
}

/** The empty draft. Exported so the dialog resets to exactly what it opened with. */
export const EMPTY_AUTHORISATION_DRAFT: AuthorisationDraft = {
  method: null,
  takenAtLocal: '',
  reference: '',
  attested: false,
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Interpret a `datetime-local` string as a real instant.
 *
 * `new Date('2026-08-15T09:30')` is parsed as LOCAL time by every browser and by
 * Node, which is what we want: the admin typed the time on their own clock. The
 * ISO string we send is therefore the correct UTC instant, not a timezone-shifted
 * one. Returns null for anything unparseable (including '').
 */
function parseLocalDateTime(value: string): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Check a lane-A draft.
 *
 * The two date rules, and why each exists:
 *  - NOT IN THE FUTURE. An authorisation dated after the moment it is being
 *    recorded is either a typo or a fiction; neither belongs in an audit row.
 *    A one-minute grace absorbs clock skew between the admin's machine and ours
 *    without opening the door to a date typed for tomorrow.
 *  - NOT OLDER THAN {@link MAX_AUTHORISATION_AGE_DAYS}. Consent to be charged
 *    goes stale. Seven days covers a callback actioned a couple of days late and
 *    refuses "he agreed last month".
 */
export function checkAuthorisation(draft: AuthorisationDraft, now: Date = new Date()): AuthorisationCheck {
  const takenAt = parseLocalDateTime(draft.takenAtLocal);

  let takenAtError: string | null = null;
  if (draft.takenAtLocal && !takenAt) {
    takenAtError = 'Enter a valid date and time.';
  } else if (takenAt) {
    // One minute of grace for clock skew; anything beyond that is a typo.
    if (takenAt.getTime() > now.getTime() + 60_000) {
      takenAtError = 'This cannot be in the future.';
    } else if (now.getTime() - takenAt.getTime() > MAX_AUTHORISATION_AGE_DAYS * MS_PER_DAY) {
      takenAtError = `Authorisation older than ${MAX_AUTHORISATION_AGE_DAYS} days is too stale to charge against. Ask the patient again.`;
    }
  }

  const referenceTrimmed = draft.reference.trim();
  const ready =
    draft.method !== null &&
    takenAt !== null &&
    takenAtError === null &&
    draft.attested === true &&
    referenceTrimmed.length <= AUTHORISATION_REFERENCE_MAX;

  return {
    ready,
    takenAtError,
    value:
      ready && draft.method && takenAt
        ? {
            method: draft.method,
            takenAt: takenAt.toISOString(),
            // Omitted rather than sent empty — contracts has it optional, and an
            // empty string would be a reference that does not exist.
            ...(referenceTrimmed ? { reference: referenceTrimmed } : {}),
            attested: true,
          }
        : null,
  };
}
